import { Badge, Button, Checkbox, Field, Input } from '@cloudflare/kumo';
import { SidePanel } from './ui/SidePanel';
import { useCallback, useEffect, useState } from 'react';
import { PasswordField } from './ui/PasswordField';
import { ICONS } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

type ProviderId = 'laya' | 'clef' | 'typesafe';
interface DecisionProvider { id: ProviderId; label: string; baseUrl: string; model: string }
interface Decision {
  enabled: boolean; provider: ProviderId; hasKey: boolean; keyFrom: 'saved' | 'server' | 'cloudflare' | null; model: string; baseUrl: string; accountId: string | null;
  inboxIntent: boolean; inboxHandoff: boolean; handoffThreshold: number;
}

const HELP: Record<ProviderId, string> = {
  laya: 'Modelo rodando na nossa stack, no serviço Laya: sem custo por uso e as mensagens não saem do servidor. Acerta menos que os outros sem treino e erra com confiança; use como sinal, não para agir sozinho.',
  clef: 'Modelo da Cloudflare (Workers AI), no formato do Jev. Pago por uso (clef-flash a US$ 0,038 e clef a US$ 0,24 por milhão de tokens), com 10 mil neurons grátis por dia. Usa a conta e o token do provedor "Cloudflare Workers AI".',
  typesafe: 'Modelo da TypeSafe, o original. Acesso antecipado; chave em console.typesafe.ai.',
};

const KEY_HINT: Record<NonNullable<Decision['keyFrom']>, string> = {
  saved: 'Chave salva aqui',
  server: 'Usando a LAYA_SECRET do servidor',
  cloudflare: 'Usando o token do provedor Cloudflare Workers AI',
};

/**
 * Plataforma › Integrações: modelo de decisão System One (Laya ou Jev).
 * Decide ação, Tool e risco da Lumi, a intenção das conversas da Caixa de
 * entrada e quando o atendente IA passa para a equipe. Não gera texto.
 */
export function DecisionModelCard() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [cfg, setCfg] = useState<Decision | null>(null);
  const [providers, setProviders] = useState<DecisionProvider[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ enabled: true, provider: 'laya' as ProviderId, apiKey: '', model: '', baseUrl: '', accountId: '', inboxIntent: true, inboxHandoff: true, threshold: '0.8' });
  const [busy, setBusy] = useState<string | null>(null);

  const apply = (body: { jev?: Decision; decisionProviders?: DecisionProvider[] }) => {
    if (body.jev) setCfg(body.jev);
    if (body.decisionProviders) setProviders(body.decisionProviders);
  };
  const load = useCallback(async () => {
    const res = await fetchApi('/api/ai/config');
    if (res.ok) apply(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const edit = () => {
    if (!cfg) return;
    // Nada configurado ainda: começa pelo Clef (sem servidor nem chave nova, se o provedor Cloudflare já existe)
    const provider = cfg.hasKey ? cfg.provider : 'clef';
    const same = provider === cfg.provider;
    const def = providers.find((p) => p.id === provider);
    setForm({
      enabled: cfg.enabled || !cfg.hasKey, provider, apiKey: '',
      model: same ? cfg.model : def?.model ?? '', baseUrl: same && cfg.baseUrl !== def?.baseUrl ? cfg.baseUrl : '',
      accountId: same ? cfg.accountId ?? '' : '',
      inboxIntent: cfg.inboxIntent, inboxHandoff: cfg.inboxHandoff, threshold: String(cfg.handoffThreshold),
    });
    setOpen(true);
  };
  const choose = (id: ProviderId) => {
    const def = providers.find((p) => p.id === id);
    setForm((f) => ({ ...f, provider: id, apiKey: '', model: id === cfg?.provider ? cfg.model : def?.model ?? '', baseUrl: '', accountId: id === cfg?.provider ? cfg.accountId ?? '' : '' }));
  };

  const req = async (key: string, url: string, init: RequestInit) => {
    setBusy(key);
    try {
      const res = await fetchApi(url, init);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível concluir');
      return body;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível concluir');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const switching = !!cfg && form.provider !== cfg.provider;
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (switching && form.provider === 'typesafe' && !form.apiKey) { toast.error('Informe a chave do Jev'); return; }
    const body = await req('save', '/api/ai/config', {
      method: 'PUT',
      body: JSON.stringify({
        jev: {
          provider: form.provider, enabled: form.enabled, model: form.model, baseUrl: form.baseUrl, accountId: form.accountId,
          inboxIntent: form.inboxIntent, inboxHandoff: form.inboxHandoff, handoffThreshold: Number(form.threshold.replace(',', '.')),
          ...(form.apiKey ? { apiKey: form.apiKey } : {}),
        },
      }),
    });
    if (body) { apply(body); setForm((f) => ({ ...f, apiKey: '' })); toast.success('Modelo de decisão salvo'); }
  };

  const test = async () => {
    const body = await req('test', '/api/ai/config/decision/test', { method: 'POST' });
    if (body) toast.success(`Respondeu em ${body.ms} ms com ${body.model}: ${Math.round((body.humano ?? 0) * 100)}% de chance de o cliente querer uma pessoa`);
  };

  const label = providers.find((p) => p.id === cfg?.provider)?.label ?? 'Modelo de decisão';
  const on = !!cfg?.enabled && !!cfg?.hasKey;

  return (
    <>
      <div className="settings-card">
        <div>
          <div className="flex items-center justify-between gap-2">
            <span style={{ height: 28, display: 'flex', alignItems: 'center', color: on ? 'var(--text)' : 'var(--purple-text)' }}><ICONS.Bulb size={32} /></span>
            {on ? <Badge variant="green">Ativo</Badge> : <Badge variant="neutral">Não configurado</Badge>}
          </div>
          <div className="font-semibold" style={{ marginTop: 12 }}>Modelo de decisão</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Decisões da Lumi, intenção na Caixa de entrada e passagem para a equipe</div>
          <div className="integration-host" title={cfg?.baseUrl || undefined}>
            <ICONS.Key size={16} />
            <span>{on ? `${label}${cfg?.provider === 'laya' ? '' : ` · ${cfg?.model}`}` : 'desligado: a Lumi usa o planejador por LLM'}</span>
          </div>
        </div>
        <div className="settings-card__actions">
          <Button variant="secondary" icon={on ? ICONS.Edit : ICONS.Plus} disabled={!cfg} onClick={edit}>
            {on ? 'Editar' : 'Configurar'}
          </Button>
        </div>
      </div>

      <SidePanel
        open={open}
        onClose={() => setOpen(false)}
        title="Modelo de decisão"
        description="Responde perguntas tipadas (escolha, nota, sim ou não) com a confiança de cada resposta, em milissegundos. Não escreve texto: quem redige continua sendo o provedor de IA."
        formId="decision-model-form"
        submitting={busy === 'save'}
        footerStart={<Button variant="secondary" icon={ICONS.Play} type="button" loading={busy === 'test'} disabled={!on || switching} title={on && !switching ? undefined : 'Salve e ative antes de testar'} onClick={test}>Testar</Button>}
      >
        <form noValidate onSubmit={save} id="decision-model-form" className="flex flex-col gap-5" data-testid="decision-model-form">
          <div className="ai-providers">
            {providers.map((p) => (
              <button key={p.id} type="button" className={`ai-providers__item ${p.id === form.provider ? 'is-selected' : ''}`} onClick={() => choose(p.id)}>
                <strong className="ai-providers__name">{p.label}</strong>
                <span>{cfg?.provider === p.id && cfg.hasKey ? (cfg.enabled ? 'Ativo' : 'Desligado') : 'Não configurado'}</span>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{HELP[form.provider]}</div>

          <div className="flex flex-col gap-4">
            <div>
              <PasswordField
                label={form.provider === 'typesafe' ? 'Chave de API' : 'Chave de API (opcional)'}
                placeholder={!switching && cfg?.hasKey ? (cfg.keyFrom ? KEY_HINT[cfg.keyFrom] : '') : ''}
                value={form.apiKey}
                onChange={(value) => setForm({ ...form, apiKey: value.trim() })}
                autoComplete="off"
              />
              {(form.provider === 'laya' || form.provider === 'clef') && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                  {form.provider === 'laya' ? 'Em branco usa a LAYA_SECRET do servidor' : 'Em branco usa o token do provedor Cloudflare Workers AI'}
                </div>
              )}
            </div>
            {form.provider !== 'laya' && (
              <Field label="Modelo" description={form.provider === 'clef' ? 'clef-flash (9B, rápido e barato) ou clef (27B)' : 'Fixe a versão (ex.: jev-1.13.0) depois de calibrar'}>
                <Input onChange={(e) => setForm({ ...form, model: e.target.value.trim() })} value={form.model} />
              </Field>
            )}
            {form.provider === 'clef' && (
              <Field label="Conta da Cloudflare (opcional)" description="Em branco usa a conta do provedor Cloudflare Workers AI">
                <Input onChange={(e) => setForm({ ...form, accountId: e.target.value.trim() })} value={form.accountId} />
              </Field>
            )}
            {form.provider !== 'clef' && (
              <Field label="Endereço (opcional)" description={form.provider === 'laya' ? 'Padrão: o serviço da stack' : 'Em branco usa o endereço oficial'}>
                <Input onChange={(e) => setForm({ ...form, baseUrl: e.target.value.trim() })} placeholder={providers.find((p) => p.id === form.provider)?.baseUrl} value={form.baseUrl} />
              </Field>
            )}
            <Field label="Limiar para passar à equipe" description="Entre 0,5 e 0,99. Mais alto = passa menos">
              <Input onChange={(e) => setForm({ ...form, threshold: e.target.value })} type="number" step={0.05} min={'0.5'} max={'0.99'} value={form.threshold} />
            </Field>
          </div>

          <div className="flex flex-col gap-2">
            <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, enabled: checked })} checked={form.enabled} label={<>Ativo</>} />
            <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, inboxIntent: checked })} checked={form.inboxIntent} label={<>Caixa de entrada: classificar a intenção e a urgência das conversas</>} />
            <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, inboxHandoff: checked })} checked={form.inboxHandoff} label={<>Atendente IA: passar para a equipe quando o cliente precisar de uma pessoa</>} />
          </div>
        </form>
      </SidePanel>
    </>
  );
}
